import {
  cloneDeep,
  compact,
  each,
  find,
  findIndex,
  has,
  map,
  reduce,
  reject,
  size,
} from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { isSanboxedFacet } from "../facets";
import { getFieldFromInnerClause, isInnerClauseFromField } from "./getter";
import { isCustomFieldsClause, isInnerClauseEmpty, isNotClause } from "./is";
import { Clause, InnerClause } from "./model";
import { refineClauseWithoutClause } from "./refiner";

function extractClauseFromField(clause: Clause, field: string): Clause {
  let andExtracted = false;
  let notExtracted = false;
  return reduce(
    clause.and,
    (nextClause, innerClause) => {
      if (isInnerClauseFromField(innerClause, field)) {
        if (isNotClause(innerClause)) {
          if (!notExtracted) {
            notExtracted = true;
            nextClause.and.push(innerClause);
          }
        } else {
          if (!andExtracted) {
            andExtracted = true;
            nextClause.and.push(innerClause);
          }
        }
      }
      return nextClause;
    },
    {
      and: [],
    }
  );
}

function extractSubClause(clause: Clause, clauses: Clause[]): Clause[] {
  const innerClause = clause.and[0];
  if (innerClause) {
    const field = getFieldFromInnerClause(innerClause);
    const subClause = extractClauseFromField(clause, field);
    let andExcluded = false;
    let notExcluded = false;
    const nexClause = {
      and: reject(clause.and, (innerClause) => {
        if (isInnerClauseFromField(innerClause, field)) {
          if (isNotClause(innerClause)) {
            if (!notExcluded) {
              notExcluded = true;
              return true;
            } else {
              return false;
            }
          } else {
            if (!andExcluded) {
              andExcluded = true;
              return true;
            } else {
              return false;
            }
          }
        } else {
          return false;
        }
      }),
    };
    return extractSubClause(nexClause, [...clauses, subClause]);
  } else {
    return clauses;
  }
}

export function extractClauses(clause: Clause) {
  const clauses =
    clause && clause.and ? extractSubClause(cloneDeep(clause), []) : [];
  return map(clauses, (_clause) => {
    return {
      clause: _clause,
      rest: refineClauseWithoutClause(clause, _clause),
    };
  });
}

export function extractFieldsFromClause(clause: Clause) {
  return compact(
    map(extractClauses(clause), ({ clause }) => {
      if (size(clause.and)) {
        return getFieldFromInnerClause(clause.and[0]);
      } else {
        return null;
      }
    })
  );
}

export function extractAndSplitClausesByFields<T extends string>(
  clause: Clause,
  fields: T[]
) {
  const clauses = extractClauses(clause);
  const permanentClauses: Partial<Record<T, { clause: Clause; rest: Clause }>> =
    {};
  function isClauseFromField(innerClause: InnerClause, field: string) {
    if (isCustomFieldsClause(innerClause)) {
      return has(innerClause.customFields, field);
    } else if (isNotClause(innerClause)) {
      return has(innerClause.not, field);
    } else if (field === Facet.MaritalStatus) {
      return (
        has(innerClause, "inRelationship") || has(innerClause, "hasChildren")
      );
    } else {
      return has(innerClause, field);
    }
  }

  const getIndex = (field: string) =>
    findIndex(clauses, (clause) => {
      if (clause) {
        return !!find(clause.clause.and, (innerClause) => {
          return isClauseFromField(innerClause, field);
        });
      } else {
        return false;
      }
    });

  const extractSandboxedFields = (field: string) => {
    const mergeSandboxedFields = (sandboxedFields: string[]) => {
      const innerClause = { [field]: [] };
      sandboxedFields.forEach((sandboxedField) => {
        const index = getIndex(sandboxedField);
        if (index > -1) {
          const isIncluded = clauses[index].clause.and.some(
            (value) => !!value[sandboxedField]
          );
          if (isIncluded) {
            innerClause[field].push(sandboxedField);
          }
          delete clauses[index];
        }
      });
      permanentClauses[field] = {
        clause: { and: [innerClause] },
        rest: {
          and: clause.and.filter(
            (inner) => !sandboxedFields.includes(Object.keys(inner)[0])
          ),
        },
      };
    };
    if (field === Facet.MaritalStatus) {
      mergeSandboxedFields(["inRelationship", "hasChildren"]);
    } else {
      return null;
    }
  };

  each(fields, (field) => {
    const index = getIndex(field);
    if (index !== -1) {
      if (isSanboxedFacet(field)) {
        extractSandboxedFields(field);
      } else {
        permanentClauses[field] = clauses[index];
        delete clauses[index];
      }
    }
  });
  return {
    permanent: permanentClauses,
    rest: compact(clauses),
  };
}

export function extractCleanedClause(clause: Clause): Clause {
  const cleanedClauses = reject(clause?.and, (innerClause) =>
    isInnerClauseEmpty(innerClause)
  );

  return { and: cleanedClauses };
}
